/**
 * Money formatting helpers.
 *
 * IMPORTANT — currency conventions in this codebase:
 *  - `IOrder.total` is stored in **cents** (it comes straight from Stripe's
 *    `session.amount_total`, which is the smallest currency unit).
 *  - `IOrder.deliveryFee` and `Bar.delivery.deliveryFee` / `minOrderAmount` /
 *    `priceMarkup` are stored in **euros** (legacy: forms expose them as
 *    decimal numbers).
 *
 * Symbol position is currency-dependent and matches the rest of the app:
 *  - `€` is rendered as a suffix with no space:  `10.00€`
 *  - `$` and `£` are rendered as a prefix with a space:  `$ 10.00`, `£ 10.00`
 *
 * Always go through these helpers when displaying or comparing money so the
 * unit is explicit at the call site.
 */

export type CurrencyCode = string | undefined

const DEFAULT_CURRENCY = '€'

function formatWithSymbol (value: number, currency: CurrencyCode): string {
  const safe = Number.isFinite(value) ? value : 0
  const symbol = currency ?? DEFAULT_CURRENCY
  if (symbol === '€') return `${safe.toFixed(2)}€`
  return `${symbol} ${safe.toFixed(2)}`
}

/** Format a value already expressed in the major unit (e.g. euros). */
export function formatMoney (amount: number, currency: CurrencyCode = DEFAULT_CURRENCY): string {
  return formatWithSymbol(amount, currency)
}

/** Format a value expressed in the minor unit (cents) — e.g. `IOrder.total`. */
export function formatCents (cents: number | null | undefined, currency: CurrencyCode = DEFAULT_CURRENCY): string {
  const value = typeof cents === 'number' && Number.isFinite(cents) ? cents / 100 : 0
  return formatMoney(value, currency)
}

/** Format a cents value using an ISO 4217 currency code from Stripe (`usd`, `gbp`, `eur`, …). */
export function formatCentsISO (cents: number | null | undefined, isoCode: string | null | undefined): string {
  const symbol = isoCodeToSymbol(isoCode)
  return formatCents(cents, symbol)
}

/** Convert cents to the major unit. Use when you need a Number, not a string. */
export function centsToMajor (cents: number | null | undefined): number {
  return typeof cents === 'number' && Number.isFinite(cents) ? cents / 100 : 0
}

function isoCodeToSymbol (isoCode: string | null | undefined): CurrencyCode {
  if (!isoCode) return DEFAULT_CURRENCY
  const code = isoCode.toLowerCase()
  if (code === 'usd') return '$'
  if (code === 'gbp') return '£'
  if (code === 'eur') return '€'
  return DEFAULT_CURRENCY
}
